import { Component, Input, Output, OnInit, OnDestroy } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { MatTableDataSource } from '@angular/material/table';
import { AuthService, SelectBox } from 'cbc-shared-lib';
import {
  Sgkb702aResInclusioncontractInclutagsupdemandctrSearchresultDto,
  Sgkb702aResInclusiveCtrInfoDto, Sgkb702aResInclusiontargetDemandctrinfoDto
} from 'sg-api';
import { UsedSgkb709aService } from '../../services/used-sgkb709a.service';
import { CompleteDataService } from '../../../../../../sg-base/sg-base-shared/pages/sgkz003d/services/complete-data.service';
import { SgkbConstains } from '../../../../../sg-kb-shared/sg-kb-constans';

interface NewSgkb702aResInclusiontargetDemandctrinfoDto extends Sgkb702aResInclusiontargetDemandctrinfoDto {
  /** 契約状態（prdBreakDateの有無、ctrendDate=99991231で表示切替） */
  keiyakuJotai?: string;
  /** エネルギー種別と熱量の区切り文字 */
  pipe?: string;
  /** 熱量の単位表示 */
  unitHeat?: string;
  /** 割引・割増金額の単位表示 */
  unitMny?: string;
  /** 割引・割増率の単位表示 */
  unitRate?: string;
}

@Component({
  selector: 'app-sgkb708d-button',
  templateUrl: './sgkb708d-button.component.html',
  styleUrl: './sgkb708d-button.component.scss'
})
export class Sgkb708dButtonComponent implements OnInit, OnDestroy {

  /** 遷移元ステータス */
  @Input() mode: string;
  /** 包括契約検索APIレスポンス */
  @Input() incCtrSearchresult: Sgkb702aResInclusioncontractInclutagsupdemandctrSearchresultDto;
  /** 包括契約情報格納FormGroup */
  @Input() registContractForm: UntypedFormGroup;
  /** 需給契約一覧テーブル */
  @Input() dataSource = new MatTableDataSource<NewSgkb702aResInclusiontargetDemandctrinfoDto>();
  /** 契約担当部署のセレクトボックスデータ */
  @Input() keiyakuTantouBushoList: SelectBox[] = [];

  private onDestroy$ = new Subject();

  /** 親コンポーネント保持領域 */
  parentForm: UntypedFormGroup | null = null;;

  /** 解約ボタン表示フラグ（初期値：非表示） */
  cancelDispFlg: boolean = false;
  /** 取消ボタン表示フラグ（初期値：非表示） */
  deleteDispFlg: boolean = false;
  /** 承認ボタン表示フラグ（初期値：非表示） */
  approveDispFlg: boolean = false;
  /** 承認ボタン権限フラグ（初期値：非表示） */
  approveAuthFlg: boolean = false;
  /** 登録ボタン表示フラグ（初期値：非表示） */
  createDispFlg: boolean = false;
  /** キャンセルボタン表示フラグ（初期値：非表示） */
  canceledDispFlg: boolean = false;
  /** 戻るボタン表示フラグ（初期値：非表示） */
  backDispFlg: boolean = false;

  constructor(
    private router: Router,
    private authService: AuthService,
    private usedsgkb709aService: UsedSgkb709aService,
    private completeDataService: CompleteDataService,
  ) { }

  /**
   * 購読を停止する処理
   * @memberof Sgkb708dButtonComponent
   */
  ngOnDestroy(): void {
    this.onDestroy$.next();
  }

  /**
   * 初期処理
   * @memberof Sgkb708dButtonComponent
   */
  ngOnInit(): void {

    // 画面項目の初期化
    this.setInitDisp();

    // 遷移パラメータが 参照 の場合
    if (this.mode === 'read') {
      // 戻るボタン表示
      this.backDispFlg = true;
    }

    // 遷移パラメータが 登録 の場合
    if (this.mode === 'create') {
      // 登録ボタン表示
      this.createDispFlg = true;
      // キャンセルボタン表示
      this.canceledDispFlg = true;
      return;
    }

    if (!this.incCtrSearchresult?.inclusiveCtrInfoList?.length) {
      return;
    }

    // 親コンポーネントで取得した包括契約検索APIレスポンスの業務情報を設定
    const incCtrInfoDto: Sgkb702aResInclusiveCtrInfoDto =
      this.incCtrSearchresult.inclusiveCtrInfoList[0];

    // 遷移パラメータが 期中変更、契約更改 のいずれかの場合
    if (this.mode === 'midterm_change' || this.mode === 'renewal') {
      // 登録申請状況コードが 2（承認済）かつ 包括契約状態コードが 2（契約中） の場合
      if (incCtrInfoDto.inclusioncontractRegistrationapplicationJokycd === SgkbConstains.CODE.HOUKATSU_CNTRCT_RGST_APLY_CD.APPROVED
        && incCtrInfoDto.inclusioncontractStcode === SgkbConstains.CODE.HOUKATSU_CNTRCT_CD.CNTRCT) {
        // 登録ボタン表示
        this.createDispFlg = true;
      }
      // キャンセルボタン表示
      this.canceledDispFlg = true;
    }

    // 遷移パラメータが 取消 の場合
    if (this.mode === 'delete') {
      // 登録申請状況コードが 1（申請中）または 解約申請状況コードが 2（申請中） の場合
      if (incCtrInfoDto.inclusioncontractRegistrationapplicationJokycd === SgkbConstains.CODE.HOUKATSU_CNTRCT_RGST_APLY_CD.APPLY
        || incCtrInfoDto.inclusioncontractBreakereqestJokycd === SgkbConstains.CODE.HOUKATSU_CANCEL_APLY_CD.APPLY) {
        // 取消ボタン表示
        this.deleteDispFlg = true;
      }
      // キャンセルボタン表示
      this.canceledDispFlg = true;
    }

    // 遷移パラメータが 解約 の場合
    if (this.mode === 'cancel') {
      // 登録申請状況コードが 2（承認済）かつ 包括契約状態コードが 2（契約中） の場合
      if (incCtrInfoDto.inclusioncontractRegistrationapplicationJokycd === SgkbConstains.CODE.HOUKATSU_CNTRCT_RGST_APLY_CD.APPROVED
        && incCtrInfoDto.inclusioncontractStcode === SgkbConstains.CODE.HOUKATSU_CNTRCT_CD.CNTRCT) {
        // 解約ボタン表示
        this.cancelDispFlg = true;
      }
      // キャンセルボタン表示
      this.canceledDispFlg = true;
    }

    // 遷移パラメータが 承認 の場合
    if (this.mode === 'approve') {
      // 登録申請状況コードが 1（申請中）または 解約申請状況コードが 2（申請中） の場合
      if (incCtrInfoDto.inclusioncontractRegistrationapplicationJokycd === SgkbConstains.CODE.HOUKATSU_CNTRCT_RGST_APLY_CD.APPLY
        || incCtrInfoDto.inclusioncontractBreakereqestJokycd === SgkbConstains.CODE.HOUKATSU_CANCEL_APLY_CD.APPLY) {
        // 承認ボタン表示
        this.approveDispFlg = true;
      }
      // キャンセルボタン表示
      this.canceledDispFlg = true;
      // TODO：認証サービス呼び出し
      // this.approveAuthFlg = this.authService.isRolesActivationDenied('SGKB708D', 'approveButton');
      this.approveAuthFlg = false;
    }
  }

  /**
   * 取消ボタン押下処理
   * @memberof Sgkb708dButtonComponent
   */
  onClickCancel(): void {

    // 包括契約登録API呼び出し（警告チェック対象外フラグ：false）
    this.usedsgkb709aService.insertContract(
      this.registContractForm.controls['registContractInfoForm'].getRawValue(),
      this.dataSource?.data, this.getSelectedViewValue(), false)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(response => {
        if (response != null) {
          this.setCompleteData(response, '取消');
        }
        else {
          console.log('SGKB709A Error.');
        }
      })

  }

  /**
   * 画面項目の初期化
   * @memberof Sgkb708dButtonComponent
   */
  setInitDisp(): void {
    // 解約ボタン表示フラグ（初期値：非表示）
    this.cancelDispFlg = false;
    // 取消ボタン表示フラグ（初期値：非表示）
    this.deleteDispFlg = false;
    // 承認ボタン表示フラグ（初期値：非表示）
    this.approveDispFlg = false;
    // 承認ボタン権限フラグ（初期値：非表示）
    this.approveAuthFlg = false;
    // 登録ボタン表示フラグ（初期値：非表示）
    this.createDispFlg = false;
    // キャンセルボタン表示フラグ（初期値：非表示）
    this.canceledDispFlg = false;
    // 戻るボタン表示フラグ（初期値：非表示）
    this.backDispFlg = false;
  }

  /**
   * 登録、解約、承認ボタン押下処理
   * @memberof Sgkb708dButtonComponent
   */
  onClickRegist(strMsg: string): void {

    // 包括契約登録API呼び出し（警告チェック対象外フラグ：false）
    this.usedsgkb709aService.insertContract(
      this.registContractForm.controls['registContractInfoForm'].getRawValue(),
      this.dataSource?.data, this.getSelectedViewValue(), false)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(response => {
        if (response != null) {
          // ワーニングを抽出
          const warningItem = response.responseCommon.errors.find(error => error.errorCode == 'SGKB5004');

          if (warningItem && warningItem != null) {
            // ワーニングメッセージを出力
            const result = window.confirm(warningItem.message);

            // ワーニングメッセージにて、OKボタンを押下した場合
            if (result) {
              // 包括契約登録API呼び出し（警告チェック対象外フラグ：true）
              this.usedsgkb709aService.insertContract(
                this.registContractForm.controls['registContractInfoForm'].getRawValue(),
                this.dataSource.data, this.getSelectedViewValue(), result)
                .pipe(takeUntil(this.onDestroy$))
                .subscribe(rerunResponse => {
                  this.setCompleteData(rerunResponse, strMsg);
                });
            } else {
              return;
            }
          } else {
            this.setCompleteData(response, strMsg);
          }
        }
        else {
          console.log('SGKB709A Error.');
        }
      })
  }

  /**
   * 登録完了画面の遷移処理
   * @memberof Sgkb708dButtonComponent
   */
  setCompleteData(response: any, strMsg: string): void {
    // 登録完了画面情報設定
    this.completeDataService.completeData = {
      title: '包括契約' + strMsg + '完了',
      message: '包括契約を' + strMsg + 'しました。',
      next: '/sgkz001d',
      koumokuList: [
        { label: '包括お客さま番号', val: response.inclusiveCtrInfo.inclusioncontractVisitornumber },
        { label: '包括契約履歴番号', val: response.inclusiveCtrInfo.inclusioncontractRrkno },
        { label: '包括契約年度内通番号', val: response.inclusiveCtrInfo.inclusioncontractYearnaiSrno },
      ],
    };
    // 登録完了画面遷移
    this.router.navigate(['/sgkz003d']);
  }



  /**
   * 契約担当部署リストの日本語名称取得処理
   * @memberof Sgkb708dButtonComponent
   */
  getSelectedViewValue(): string {
    // 選択されている契約担当部署のvalueを取得
    const selectedValue: string =
      this.registContractForm.controls['registContractInfoForm'].get('keiyakuTantouBushoCategory')?.value;
    // 契約担当部署リストから選択済みの契約担当部署名称を取得
    return this.keiyakuTantouBushoList.find(item => item.value === selectedValue)?.viewValue;
  }

  /**
   * キャンセルボタン押下処理
   * @memberof Sgkb708dButtonComponent
   */
  onClickCansel(): void {
    if (this.mode === 'create') {
      this.router.navigate(['/sgkz001d']);
    } else {
      this.router.navigate(['/sgkb/sgkb701d', {
        mode: this.mode || ""
      }]);
    }

  }

  /**
   * 戻るボタン押下処理
   * @memberof Sgkb708dButtonComponent
   */
  goToPrevious(): void {
    this.router.navigate(['/sgkb/sgkb701d', {
      mode: this.mode || ""
    }]);
  }

}
